import React from 'react'
import { useState, useEffect, useRef } from 'react'
import {
  Button,
  Modal,
  Spinner,
  Table,
  Form,
  Tooltip,
  useToaster,
  Grid,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { CautionIcon } from '@enterprise-ui/icons'
import { DatePicker } from '@enterprise-ui/canvas-ui-react-datepicker'
import { getLocationLevelCosts } from '../services/requests'
import {
  isAValidCostChange,
  newCostExceedsCostThresholds,
} from '../services/helperFunctions'
import {
  TOASTER_DEFAULTS,
  LOCATION_LEVEL_MODAL_ITEM_FIELDS,
  LOCATION_LEVEL_MODAL_TABLE_FIELDS,
  COST_LOCATION_GROUP_VALUES,
  LOCATION_TYPE_TO_COST_LOCATION_GROUP_TYPE,
  COST_SEARCH_VIEW_BY_OPTIONS,
  COST_SEARCH_VIEW_BY_OPTIONS_DVS,
  DATA_TABLE_FIELDS,
  getThresholdWarningText,
  noCasepackExplanationText,
  getInvalidCostErrorText,
  getCurrentUTCDateString,
  getCurrentLocalDateString,
  convertDateToUTCDateString,
  convertDateToLocalDateStringWithBackendFormatting,
} from '../globalConfig/constants'
import { StartDateTimezoneMismatchExplanationModal } from './StartDateTimezoneMismatchExplanationModal'
import { VendorContext } from '../globalState/Vendor/index'
import cloneDeep from 'lodash/cloneDeep'

export const LocationLevelTableModal = (props) => {
  const { parentRowData, isVisible, onRefuse, onSubmit, existingSelections } =
    props
  const [tableData, setTableData] = useState(null)
  const [columnDefs, setColumnDefs] = useState(null)
  const [rowData, setRowData] = useState(null)
  const [changeCount, setChangeCount] = useState(0)
  const [initialRowData, setInitialRowData] = useState(null)
  const [, setLastUsedStartDate] = useState(null)
  const [isTimezoneMismatchModalVisible, setIsTimezoneMismatchModalVisible] =
    useState(null)
  const makeToast = useToaster()
  const [state] = React.useContext(VendorContext)
  const startDateRefs = useRef([])
  const endDateRefs = useRef([])

  useEffect(() => {
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to get location level cost data',
      })
    }
    const constructRequestBody = (parentData) => {
      const ALL_COST_SEARCH_VIEW_BY_OPTIONS = {
        ...COST_SEARCH_VIEW_BY_OPTIONS,
        ...COST_SEARCH_VIEW_BY_OPTIONS_DVS,
      }
      const returnBody = {}
      returnBody.costLocationGroup = {
        [LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
          .variableName]:
          parentData?.[
            LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
              .variableName
          ],
        [COST_LOCATION_GROUP_VALUES.variableName]:
          parentData?.[
            DATA_TABLE_FIELDS.COST_LOCATION_GROUP_VALUE.variableName
          ] === COST_SEARCH_VIEW_BY_OPTIONS.CHAIN.variableName ||
          parentData?.[
            DATA_TABLE_FIELDS.COST_LOCATION_GROUP_VALUE.variableName
          ] === ALL_COST_SEARCH_VIEW_BY_OPTIONS.SUPPLIER_CHAIN.variableName
            ? []
            : [
                parentData?.[
                  DATA_TABLE_FIELDS.COST_LOCATION_GROUP_VALUE.variableName
                ],
              ],
        ...([
          ALL_COST_SEARCH_VIEW_BY_OPTIONS.SUPPLIER_CHAIN.variableName,
          ALL_COST_SEARCH_VIEW_BY_OPTIONS.SUPPLIER_STATE.variableName,
          ALL_COST_SEARCH_VIEW_BY_OPTIONS.SUPPLIER_LOCATION.variableName,
        ].includes(
          parentData?.[
            LOCATION_LEVEL_MODAL_TABLE_FIELDS.COST_LOCATION_GROUP_TYPE
              .variableName
          ],
        )
          ? { vendorId: state?.selectedVendor }
          : {}),
      }
      returnBody.costIds = parentRowData?.costIds
      return returnBody
    }
    if (parentRowData) {
      getLocationLevelCosts(
        state?.selectedVendor,
        constructRequestBody(parentRowData),
      )
        .then((response) => {
          if (response?.status === 200) {
            setTableData(response?.data)
          } else {
            sendFailureToast()
            onRefuse()
          }
        })
        .catch(() => {
          sendFailureToast()
          onRefuse()
        })
    } else {
      setColumnDefs(null)
      setRowData(null)
      setInitialRowData(null)
      setChangeCount(0)
      setTableData(null)
      startDateRefs.current = []
      endDateRefs.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentRowData])

  useEffect(() => {
    if (tableData) {
      constructColumnDefs()
      startRowDataConstruction(tableData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData])

  useEffect(() => {
    if (rowData) {
      let additionCount = 0
      let removalCount = 0
      let changedRowDataCount = 0
      rowData.forEach((row, rowIndex) => {
        if (
          isAValidCostChange(row) &&
          !isAValidCostChange(initialRowData?.[rowIndex])
        ) {
          additionCount++
        }
        if (
          isAValidCostChange(row) &&
          existingSelections &&
          isAValidCostChange(initialRowData?.[rowIndex]) &&
          (row?.new_start_date?.cellValue !==
            initialRowData?.[rowIndex]?.new_start_date?.cellValue ||
            row?.new_end_date?.cellValue !==
              initialRowData?.[rowIndex]?.new_end_date?.cellValue ||
            row?.new_unit_cost?.cellValue !==
              initialRowData?.[rowIndex]?.new_unit_cost?.cellValue ||
            row?.new_casepack_cost?.cellValue !==
              initialRowData?.[rowIndex]?.new_casepack_cost?.cellValue)
        ) {
          changedRowDataCount++
        }
        if (
          !isAValidCostChange(row) &&
          isAValidCostChange(initialRowData?.[rowIndex])
        ) {
          removalCount++
        }
      })
      setChangeCount(additionCount + changedRowDataCount + removalCount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData])

  const roundNumber = (value, precision) => {
    var multiplier = Math.pow(10, precision || 0)
    return Math.round(value * multiplier) / multiplier
  }

  const getUnitCostInput = (
    e,
    newValue,
    rowIndex,
    casepackQuantity,
    oldCost,
    startDate,
  ) => {
    return (
      <span className="hesp-general-flex">
        <Form.Field
          className="hesp-dataTable-inputColumn"
          id={`${rowIndex}_location_level_new_unit_cost`}
          data-testid={`${rowIndex}_location_level_new_unit_cost`}
          onChange={(event) => {
            if (
              event?.target?.value.split('.')?.[1]?.length > 6 ||
              event?.nativeEvent?.data?.toUpperCase() === 'E' ||
              event?.target?.value < 0
            ) {
              return
            }
            const numberInput = event?.target?.value
              ? Number(event.target.value)
              : ''
            setLastUsedStartDate((prevStartDate) => {
              setRowData((prevRowData) => {
                const newArray = Array.from(prevRowData)
                newArray[rowIndex].new_unit_cost.cellValue =
                  numberInput || numberInput === 0 ? numberInput : ''
                newArray[rowIndex].new_unit_cost.cellDisplay = getUnitCostInput(
                  event,
                  numberInput || numberInput === 0 ? numberInput : '',
                  rowIndex,
                  casepackQuantity,
                  oldCost,
                  prevRowData[rowIndex]?.new_start_date?.cellValue,
                )
                newArray[rowIndex].new_casepack_cost.cellValue =
                  numberInput || numberInput === 0
                    ? numberInput * casepackQuantity
                    : ''
                newArray[rowIndex].new_casepack_cost.cellDisplay =
                  getCasepackCostInput(
                    event,
                    numberInput || numberInput === 0
                      ? numberInput * casepackQuantity
                      : '',
                    rowIndex,
                    casepackQuantity,
                    oldCost,
                    prevRowData[rowIndex]?.new_start_date?.cellValue,
                  )
                newArray[rowIndex].new_start_date.cellDisplay =
                  getStartDateInput(
                    prevRowData[rowIndex].new_start_date.cellValue ||
                      prevStartDate,
                    rowIndex,
                    prevRowData[rowIndex].new_end_date.cellValue,
                    numberInput || numberInput === 0 ? numberInput : '',
                  )
                return newArray
              })
              return prevStartDate
            })
          }}
          value={newValue || newValue === 0 ? roundNumber(newValue, 6) : ''}
          highlight={newCostExceedsCostThresholds(oldCost, newValue)}
          error={(startDate && !newValue) || newValue === 0}
          type="number"
          step="any"
          placeholder="$"
          onWheel={(e) => e.target.blur()}
        />
        {newCostExceedsCostThresholds(oldCost, newValue) ? (
          <Tooltip
            className="hesp-general-wordBreak-keepAll hesp-general-flex"
            content={getThresholdWarningText()}
            location="bottom"
          >
            <Button
              className="hesp-dataTable-thresholdAlert"
              data-testid={`${rowIndex}-locationLevel-newUnitCost-thresholdWarning`}
              iconOnly
              type="ghost"
              aria-label="Threshold Warning Icon"
            >
              <EnterpriseIcon icon={CautionIcon} />
            </Button>
          </Tooltip>
        ) : null}
        {newValue === 0 ? (
          <Tooltip
            className="hesp-general-wordBreak-keepAll hesp-general-flex"
            content={getInvalidCostErrorText()}
            location="bottom"
          >
            <Button
              className="hesp-dataTable-invalidCostError"
              data-testid={`${rowIndex}-newUnitCost-invalidCostError`}
              iconOnly
              type="ghost"
              aria-label="Invalid Cost Error Icon"
            >
              <EnterpriseIcon icon={CautionIcon} />
            </Button>
          </Tooltip>
        ) : null}
      </span>
    )
  }

  const getCasepackCostInput = (
    e,
    newValue,
    rowIndex,
    casepackQuantity,
    oldCost,
    startDate,
  ) => {
    if (casepackQuantity) {
      return (
        <span className="hesp-general-flex">
          <Form.Field
            className="hesp-dataTable-inputColumn"
            id={`${rowIndex}_location_level_new_casepack_cost`}
            data-testid={`${rowIndex}_location_level_new_casepack_cost`}
            onChange={(event) => {
              if (
                event?.target?.value.split('.')?.[1]?.length > 6 ||
                event?.nativeEvent?.data?.toUpperCase() === 'E' ||
                event?.target?.value < 0
              ) {
                return
              }
              const numberInput = event?.target?.value
                ? Number(event.target.value)
                : ''
              setLastUsedStartDate((prevStartDate) => {
                setRowData((prevRowData) => {
                  const newArray = Array.from(prevRowData)
                  newArray[rowIndex].new_casepack_cost.cellValue =
                    numberInput || numberInput === 0 ? numberInput : ''
                  newArray[rowIndex].new_casepack_cost.cellDisplay =
                    getCasepackCostInput(
                      event,
                      numberInput || numberInput === 0 ? numberInput : '',
                      rowIndex,
                      casepackQuantity,
                      oldCost,
                      prevRowData[rowIndex]?.new_start_date?.cellValue,
                    )
                  newArray[rowIndex].new_unit_cost.cellValue =
                    numberInput || numberInput === 0
                      ? numberInput / casepackQuantity
                      : ''
                  newArray[rowIndex].new_unit_cost.cellDisplay =
                    getUnitCostInput(
                      event,
                      numberInput || numberInput === 0
                        ? numberInput / casepackQuantity
                        : '',
                      rowIndex,
                      casepackQuantity,
                      oldCost,
                      prevRowData[rowIndex]?.new_start_date?.cellValue,
                    )
                  newArray[rowIndex].new_start_date.cellDisplay =
                    getStartDateInput(
                      prevRowData[rowIndex].new_start_date.cellValue ||
                        prevStartDate,
                      rowIndex,
                      prevRowData[rowIndex].new_end_date.cellValue,
                      numberInput || numberInput === 0
                        ? numberInput / casepackQuantity
                        : '',
                    )
                  return newArray
                })
                return prevStartDate
              })
            }}
            value={newValue || newValue === 0 ? roundNumber(newValue, 6) : ''}
            highlight={newCostExceedsCostThresholds(
              oldCost,
              newValue || newValue === 0 ? newValue / casepackQuantity : '',
            )}
            error={(startDate && !newValue) || newValue === 0}
            type="number"
            step="any"
            placeholder="$"
            onWheel={(e) => e.target.blur()}
          />
          {newCostExceedsCostThresholds(
            oldCost,
            newValue || newValue === 0 ? newValue / casepackQuantity : '',
          ) ? (
            <Tooltip
              className="hesp-general-wordBreak-keepAll hesp-general-flex"
              content={getThresholdWarningText()}
              location="bottom"
            >
              <Button
                className="hesp-dataTable-thresholdAlert"
                data-testid={`${rowIndex}-locationLevel-newCasepackCost-thresholdWarning`}
                iconOnly
                type="ghost"
                aria-label="Threshold Warning Icon"
              >
                <EnterpriseIcon icon={CautionIcon} />
              </Button>
            </Tooltip>
          ) : null}
          {newValue === 0 ? (
            <Tooltip
              className="hesp-general-wordBreak-keepAll hesp-general-flex"
              content={getInvalidCostErrorText()}
              location="bottom"
            >
              <Button
                className="hesp-dataTable-invalidCostError"
                data-testid={`${rowIndex}-newUnitCost-invalidCostError`}
                iconOnly
                type="ghost"
                aria-label="Invalid Cost Error Icon"
              >
                <EnterpriseIcon icon={CautionIcon} />
              </Button>
            </Tooltip>
          ) : null}
        </span>
      )
    } else {
      return (
        <Tooltip
          className="hesp-general-wordBreak-keepAll hesp-general-block"
          content={noCasepackExplanationText}
          location="bottom"
        >
          <div className="hesp-general-ellipsedText">N/A</div>
        </Tooltip>
      )
    }
  }

  const getStartDateInput = (newValue, rowIndex, endDate, unitCost) => {
    return (
      <span
        onClick={() => {
          setTimeout(() => {
            startDateRefs?.current?.[rowIndex]?.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
          }, 100)
        }}
        ref={startDateRefs?.current?.[rowIndex]}
      >
        <DatePicker
          className="hesp-dataTable-inputColumn"
          id={`${rowIndex}_location_level_new_start_date`}
          key={`${rowIndex}_location_level_new_start_date`}
          data-testid={`${rowIndex}_location_level_new_start_date`}
          location="bottom-left"
          error={unitCost && !newValue}
          onUpdate={(id, value) => {
            setLastUsedStartDate(value)
            setRowData((prevRowData) => {
              const newArray = Array.from(prevRowData)
              newArray[rowIndex].new_start_date.cellValue = value
              newArray[rowIndex].new_start_date.cellDisplay = getStartDateInput(
                value,
                rowIndex,
                endDate,
                prevRowData[rowIndex]?.new_unit_cost?.cellValue,
              )
              newArray[rowIndex].new_end_date.cellDisplay = getEndDateInput(
                endDate,
                rowIndex,
                value,
              )
              newArray[rowIndex].new_unit_cost.cellDisplay = getUnitCostInput(
                null,
                prevRowData[rowIndex].new_unit_cost.cellValue,
                rowIndex,
                parentRowData[
                  LOCATION_LEVEL_MODAL_ITEM_FIELDS?.CASEPACK_QUANTITY
                    ?.variableName
                ],
                prevRowData[rowIndex]?.[
                  LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName
                ]?.cellValue,
                value,
              )
              newArray[rowIndex].new_casepack_cost.cellDisplay =
                getCasepackCostInput(
                  null,
                  prevRowData[rowIndex].new_casepack_cost.cellValue,
                  rowIndex,
                  parentRowData[
                    LOCATION_LEVEL_MODAL_ITEM_FIELDS?.CASEPACK_QUANTITY
                      ?.variableName
                  ],
                  prevRowData[rowIndex]?.[
                    LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName
                  ]?.cellValue,
                  value,
                )
              return newArray
            })
          }}
          disableDates={{
            before: getCurrentUTCDateString(),
            after: endDate,
          }}
          value={newValue}
        />
      </span>
    )
  }

  const getEndDateInput = (newValue, rowIndex, startDate) => {
    return (
      <span
        onClick={() => {
          setTimeout(() => {
            endDateRefs?.current?.[rowIndex]?.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
          }, 100)
        }}
        ref={endDateRefs?.current?.[rowIndex]}
      >
        <DatePicker
          className="hesp-dataTable-inputColumn"
          id={`${rowIndex}_location_level_new_end_date`}
          key={`${rowIndex}_location_level_new_end_date`}
          data-testid={`${rowIndex}_location_level_new_end_date`}
          location="bottom-left"
          onUpdate={(id, value) => {
            setRowData((prevRowData) => {
              const newArray = Array.from(prevRowData)
              newArray[rowIndex].new_end_date.cellValue = value
              newArray[rowIndex].new_end_date.cellDisplay = getEndDateInput(
                value,
                rowIndex,
                startDate,
              )
              newArray[rowIndex].new_start_date.cellDisplay = getStartDateInput(
                startDate,
                rowIndex,
                value,
              )
              return newArray
            })
          }}
          disableDates={{
            before: startDate || getCurrentUTCDateString(),
          }}
          value={newValue}
          placeholder="12/31/9999"
        />
      </span>
    )
  }

  const constructColumnDefs = () => {
    const newColumnDefs = []
    const allHeaderVariableNames = Object?.values(
      LOCATION_LEVEL_MODAL_TABLE_FIELDS,
    ).reduce((self, header) => ({ ...self, [header.variableName]: header }), {})
    Object.keys(allHeaderVariableNames).forEach((rowDataVariable) => {
      if (allHeaderVariableNames?.[rowDataVariable]) {
        newColumnDefs.push({
          field: rowDataVariable,
          headerName:
            rowDataVariable ===
              LOCATION_LEVEL_MODAL_TABLE_FIELDS?.NEW_START_DATE_INPUT
                ?.variableName &&
            getCurrentUTCDateString() !== getCurrentLocalDateString() ? (
              <span className="hesp-general-flex hesp-general-justifyCenter">
                {allHeaderVariableNames?.[rowDataVariable]?.label}{' '}
                <Button
                  className="hesp-dataTable-thresholdAlert"
                  data-testid="startDate-timezone-mismatch-warning"
                  iconOnly
                  type="ghost"
                  aria-label="Timezone Mismatch Warning Icon"
                  onClick={() => {
                    setIsTimezoneMismatchModalVisible(true)
                  }}
                >
                  <EnterpriseIcon icon={CautionIcon} />
                </Button>
              </span>
            ) : (
              allHeaderVariableNames?.[rowDataVariable]?.label
            ),
          width:
            allHeaderVariableNames?.[rowDataVariable]?.tableDetails
              ?.defaultColumnWidth,
          align: allHeaderVariableNames?.[rowDataVariable]?.tableDetails?.align,
          sortable:
            allHeaderVariableNames?.[rowDataVariable]?.tableDetails?.sortable,
          sortType:
            allHeaderVariableNames?.[rowDataVariable]?.tableDetails?.sortType,
          isRowHeader:
            rowDataVariable ===
            LOCATION_LEVEL_MODAL_TABLE_FIELDS.LOCATION_ID.variableName,
        })
      }
    })
    const newSortedColumnDefs = Object.keys(allHeaderVariableNames)
      .filter(function (i) {
        return Object.keys(allHeaderVariableNames).includes(i)
      })
      .map((i) => newColumnDefs.find((j) => j?.field === i))
    setColumnDefs(newSortedColumnDefs)
  }

  const startRowDataConstruction = (data) => {
    constructRowData(data)
      .then((constructedData) => {
        setRowData(constructedData)
        setInitialRowData(cloneDeep(constructedData))
      })
      .catch(() => {
        if (isVisible) {
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'error',
            heading: 'Error',
            message: 'Failed to parse data into a table',
          })
          onRefuse()
        }
      })
  }

  const constructRowData = (data) => {
    return new Promise(function (resolve, reject) {
      const newRowData = []
      const allFieldVariableNames = Object.values(
        LOCATION_LEVEL_MODAL_TABLE_FIELDS,
      ).reduce((self, field) => ({ ...self, [field.variableName]: field }), {})
      data.forEach((dataRow, rowIndex) => {
        startDateRefs.current.push(React.createRef())
        endDateRefs.current.push(React.createRef())
        const formattedRow = {}
        const hasExistingValues = Object.keys(
          existingSelections || {},
        ).includes(
          dataRow?.[
            LOCATION_LEVEL_MODAL_TABLE_FIELDS?.LOCATION_ID?.variableName
          ]?.toString(),
        )
        const existingStartDate = hasExistingValues
          ? convertDateToUTCDateString(
              new Date(
                existingSelections[
                  dataRow?.[
                    LOCATION_LEVEL_MODAL_TABLE_FIELDS?.LOCATION_ID?.variableName
                  ]
                ]?.startDate,
              ),
            )
          : null
        const existingEndDate = hasExistingValues
          ? convertDateToUTCDateString(
              new Date(
                existingSelections[
                  dataRow?.[
                    LOCATION_LEVEL_MODAL_TABLE_FIELDS?.LOCATION_ID?.variableName
                  ]
                ]?.endDate,
              ),
            )
          : null
        const existingUnitCost = hasExistingValues
          ? existingSelections[
              dataRow?.[
                LOCATION_LEVEL_MODAL_TABLE_FIELDS?.LOCATION_ID?.variableName
              ]
            ]?.cost
          : null
        const existingCasepackCost = hasExistingValues
          ? existingSelections[
              dataRow?.[
                LOCATION_LEVEL_MODAL_TABLE_FIELDS?.LOCATION_ID?.variableName
              ]
            ]?.cost *
            parentRowData?.[
              LOCATION_LEVEL_MODAL_ITEM_FIELDS?.CASEPACK_QUANTITY?.variableName
            ]
          : null

        formattedRow.new_start_date = {
          cellValue: hasExistingValues ? existingStartDate : '',
          cellDisplay: hasExistingValues
            ? getStartDateInput(
                existingStartDate,
                rowIndex,
                existingEndDate === '12/31/9999' ? null : existingEndDate,
              )
            : getStartDateInput('', rowIndex),
        }
        formattedRow.new_end_date = {
          cellValue: hasExistingValues ? existingEndDate : '',
          cellDisplay: hasExistingValues
            ? getEndDateInput(
                existingEndDate === '12/31/9999' ? null : existingEndDate,
                rowIndex,
                existingStartDate,
              )
            : getEndDateInput('', rowIndex),
        }
        formattedRow.new_unit_cost = {
          cellValue: hasExistingValues ? existingUnitCost : '',
          cellDisplay: hasExistingValues
            ? getUnitCostInput(
                '',
                existingUnitCost,
                rowIndex,
                parentRowData[
                  LOCATION_LEVEL_MODAL_ITEM_FIELDS?.CASEPACK_QUANTITY
                    ?.variableName
                ],
                dataRow[LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName],
              )
            : getUnitCostInput(
                '',
                '',
                rowIndex,
                parentRowData[
                  LOCATION_LEVEL_MODAL_ITEM_FIELDS?.CASEPACK_QUANTITY
                    ?.variableName
                ],
                dataRow[LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName],
              ),
        }
        formattedRow.new_casepack_cost = {
          cellValue: hasExistingValues ? existingCasepackCost : '',
          cellDisplay: hasExistingValues
            ? getCasepackCostInput(
                '',
                existingCasepackCost,
                rowIndex,
                parentRowData[
                  LOCATION_LEVEL_MODAL_ITEM_FIELDS?.CASEPACK_QUANTITY
                    ?.variableName
                ],
                dataRow[LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName],
              )
            : getCasepackCostInput(
                '',
                '',
                rowIndex,
                parentRowData[
                  LOCATION_LEVEL_MODAL_ITEM_FIELDS?.CASEPACK_QUANTITY
                    ?.variableName
                ],
                dataRow[LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName],
              ),
        }
        Object.keys(allFieldVariableNames).forEach((dataRowKey) => {
          if (
            ![
              LOCATION_LEVEL_MODAL_TABLE_FIELDS?.NEW_START_DATE_INPUT
                ?.variableName,
              LOCATION_LEVEL_MODAL_TABLE_FIELDS?.NEW_END_DATE_INPUT
                ?.variableName,
              LOCATION_LEVEL_MODAL_TABLE_FIELDS?.NEW_UNIT_COST_INPUT
                ?.variableName,
              LOCATION_LEVEL_MODAL_TABLE_FIELDS?.NEW_CASEPACK_COST_INPUT
                ?.variableName,
            ].includes(dataRowKey)
          ) {
            formattedRow[dataRowKey] = {
              cellValue: dataRow[dataRowKey],
              cellDisplay: (
                <div
                  className={
                    allFieldVariableNames[dataRowKey]?.tableDetails
                      ?.cellClassName
                  }
                >
                  {allFieldVariableNames[dataRowKey]?.tableDetails?.toStringFunc
                    ? allFieldVariableNames[
                        dataRowKey
                      ]?.tableDetails?.toStringFunc(dataRow[dataRowKey])
                    : dataRow[dataRowKey]}
                </div>
              ),
            }
          }
        })
        newRowData.push(formattedRow)
      })
      resolve(newRowData)
    })
  }

  const convertToCostChangeObject = (dataRows) => {
    const returnObject = {}
    dataRows.forEach((row) => {
      if (isAValidCostChange(row)) {
        returnObject[
          row[
            LOCATION_LEVEL_MODAL_TABLE_FIELDS?.LOCATION_ID?.variableName
          ].cellValue
        ] = {
          [LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName]: roundNumber(
            row.new_unit_cost.cellValue,
            6,
          ),
          startDate: convertDateToLocalDateStringWithBackendFormatting(
            new Date(row.new_start_date.cellValue),
          ),
          endDate: row.new_end_date.cellValue
            ? convertDateToLocalDateStringWithBackendFormatting(
                new Date(row.new_end_date.cellValue),
              )
            : '9999-12-31',
          costLocationGroup: {
            [LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST_LOCATION_GROUP_TYPE
              ?.variableName]:
              LOCATION_TYPE_TO_COST_LOCATION_GROUP_TYPE[
                row?.locationTypeCode?.cellValue
              ],
            [COST_LOCATION_GROUP_VALUES?.variableName]: [
              row?.locationId?.cellValue,
            ],
            ...(LOCATION_TYPE_TO_COST_LOCATION_GROUP_TYPE[
              row?.locationTypeCode?.cellValue
            ] === 'SUPPLIER_LOCATION' && { vendorId: state.selectedVendor }),
          },
          [LOCATION_LEVEL_MODAL_ITEM_FIELDS?.TCIN?.variableName]:
            parentRowData?.[
              LOCATION_LEVEL_MODAL_ITEM_FIELDS?.TCIN?.variableName
            ],
          vendorIds: [state.selectedVendor],
          previousCost:
            row?.[LOCATION_LEVEL_MODAL_TABLE_FIELDS?.COST?.variableName]
              ?.cellValue,
        }
      }
    })
    return returnObject
  }

  const getCombinedColumnWidths = () => {
    let widthTotal = 0
    Object.values(LOCATION_LEVEL_MODAL_TABLE_FIELDS).forEach((field) => {
      if (field?.tableDetails?.defaultColumnWidth) {
        widthTotal = widthTotal + field.tableDetails.defaultColumnWidth
      } else {
        widthTotal = widthTotal + 250
      }
    })
    return `${widthTotal}px`
  }

  return (
    <Modal
      className="hesp-dataTable-locationLevelModal-modalContainer"
      isVisible={isVisible}
      onRefuse={onRefuse}
      onSubmit={onSubmit}
      headingText={
        parentRowData
          ? parentRowData[
              LOCATION_LEVEL_MODAL_ITEM_FIELDS.ITEM_NAME.variableName
            ]
          : ''
      }
      size="expanded"
    >
      <div className="hc-pa-normal">
        <div
          className="hesp-general-flex hesp-general-flex-wrap hc-ph-normal hc-pb-normal"
          style={{ justifyContent: 'flex-start' }}
        >
          <div
            key={`locationLevelModal-itemData`}
            className="hesp-dataTable-locationLevelModal-itemData"
          >
            {Object.values(LOCATION_LEVEL_MODAL_ITEM_FIELDS).map(
              (locationLevelField, index) => {
                if (
                  parentRowData &&
                  locationLevelField.keyName !== 'ITEM_NAME'
                ) {
                  return (
                    <>
                      <strong>{`${locationLevelField.label}: `}</strong>
                      {locationLevelField?.tableDetails?.toStringFunc
                        ? `${locationLevelField.tableDetails.toStringFunc(
                            parentRowData[locationLevelField.variableName],
                          )}   `
                        : `${parentRowData[locationLevelField.variableName]}   `}
                    </>
                  )
                } else {
                  return null
                }
              },
            )}
          </div>
        </div>
        {rowData && columnDefs ? (
          <>
            <Grid.Container className="hc-pa-normal">
              <Grid.Item className="hesp-general-fullwidth hesp-position-relative hesp-dataTable-locationLevelModal-tableContainer hc-pa-none">
                <Table
                  className="hesp-table-header hesp-cost-search-table"
                  data-testid="location-level-table"
                  name="Location Level Data Table"
                  id="location-level-table"
                  cellPadding="dense"
                  height={500}
                  style={{ minHeight: '400px' }}
                  fixedHeader
                  data={{
                    defaultColDef: { sortable: false, resizable: true },
                    columnDefs: columnDefs,
                    rowData: rowData,
                  }}
                  roundCorner
                  pagination={{
                    type: 'client',
                    pageSizeOptions: [10, 25, 50, 100, 500],
                    initialPageSize: 50,
                  }}
                  scrollableWidth
                  alternateRowColor
                />
                {rowData.length === 0 ? (
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%',
                      textAlign: 'center',
                      bottom: '325px',
                    }}
                  >
                    {'No results found'}
                  </span>
                ) : null}
              </Grid.Item>
            </Grid.Container>
          </>
        ) : (
          <div
            className="hesp-general-fullwidth hesp-position-relative hc-mb-normal"
            style={{
              height: '570px',
              width: getCombinedColumnWidths(),
              maxWidth: 'calc(80vw - 24px)',
            }}
          >
            <span
              data-testid="location-level-table-upload-lockout"
              className="hesp-lockout-container hesp-lockout-overlay"
            ></span>
            <span className={'hesp-lockout-container'}>
              <Spinner
                data-testid="location-level-table-spinner"
                size="expanded"
              />
            </span>
          </div>
        )}
        <div className="hesp-general-flex hesp-general-flex-direction-reverse">
          <Button
            data-testid="add-cost-update-button"
            type="secondary"
            disabled={changeCount < 1}
            onClick={() => {
              onSubmit(convertToCostChangeObject(rowData), parentRowData.index)
            }}
          >
            {`ADD ${changeCount} UPDATE${changeCount === 1 ? '' : 'S'}`}
          </Button>
          <Button
            style={{ marginRight: '12px' }}
            data-testid="cancel-location-level-button"
            type="ghost"
            onClick={() => {
              onRefuse()
            }}
          >
            CANCEL
          </Button>
        </div>
      </div>
      <StartDateTimezoneMismatchExplanationModal
        isVisible={isTimezoneMismatchModalVisible}
        onRefuse={() => {
          setIsTimezoneMismatchModalVisible(false)
        }}
      />
    </Modal>
  )
}
